import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { 
  ArrowRight, 
  Code2, 
  Smartphone, 
  Zap, 
  Shield, 
  Star, 
  CheckCircle2,
  Globe,
  Sparkles,
  Clock,
  Users
} from 'lucide-react';

const features = [
  {
    icon: Sparkles,
    title: 'AI-Powered Design',
    description: 'Our AI analyzes your business and creates a unique, professional website tailored to your brand.'
  },
  {
    icon: Clock,
    title: 'Lightning Fast Delivery',
    description: 'Get your website up and running in days, not weeks. We believe in rapid deployment.'
  },
  {
    icon: Shield,
    title: 'Secure & Reliable',
    description: 'Enterprise-grade security with 99.9% uptime guarantee. Your site is in safe hands.'
  },
  {
    icon: Users,
    title: 'Dedicated Support',
    description: '24/7 support from our expert team. We\'re here whenever you need us.'
  }
];

const testimonials = [
  {
    name: 'Sarah Mitchell',
    role: 'Owner, The Golden Spoon Cafe',
    content: 'TryWeb transformed my business! Within a week, I had a stunning website that perfectly represents my cafe. Orders have increased by 40%.',
    rating: 5
  },
  {
    name: 'James Rodriguez',
    role: 'Founder, Rodriguez Auto Services',
    content: 'I was skeptical about AI-generated websites, but TryWeb exceeded all expectations. Professional, fast, and affordable.',
    rating: 5
  },
  {
    name: 'Emma Thompson',
    role: 'CEO, Thompson Legal Services',
    content: 'The team at TryWeb understood exactly what my law firm needed. The website looks premium and has brought in numerous new clients.',
    rating: 5
  }
];

const stats = [
  { value: '£59.99', label: 'Per Month Only' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '48h', label: 'Average Delivery' },
  { value: '24/7', label: 'Support Available' }
];

const HomePage = () => {
  useEffect(() => {
    document.title = 'TryWeb | Professional Website Design for UK Businesses';
  }, []);

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center hero-glow noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-gradient">Transform Your Business</span>
                <br />
                <span className="text-foreground">With a Stunning Website</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg">
                We create stunning, professional websites and mobile apps using cutting-edge AI technology. 
                Get online in days, not weeks, at a fraction of the traditional cost.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/pricing">
                  <Button size="lg" className="glow-primary gap-2" data-testid="hero-cta">
                    Get Your Website <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button size="lg" variant="outline" data-testid="hero-secondary">
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative hidden lg:block">
              {/* Ambient glow */}
              <div className="absolute -inset-10 bg-[radial-gradient(circle_at_30%_30%,rgba(45,212,191,0.25),transparent_60%),radial-gradient(circle_at_75%_70%,rgba(244,114,182,0.2),transparent_60%)] blur-2xl" aria-hidden="true" />

              {/* Layered promo composition */}
              <div className="relative h-[460px] max-w-[480px] mx-auto">
                {/* Stir promo (back, rotated left) */}
                <a
                  href="https://stir-it.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-0 left-0 w-[52%] aspect-[9/19] rounded-[2rem] overflow-hidden border-[8px] border-zinc-900 shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-500"
                  data-testid="hero-stir-promo"
                  aria-label="Stir app"
                >
                  <img src="/apps/stir-hero.jpg" alt="Stir — recipe import promo" className="w-full h-full object-cover" />
                </a>

                {/* Kisses promo (front, rotated right) */}
                <a
                  href="https://kisses-id.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-0 right-0 w-[52%] aspect-[9/19] rounded-[2rem] overflow-hidden border-[8px] border-zinc-900 shadow-2xl rotate-[6deg] hover:rotate-0 transition-transform duration-500"
                  data-testid="hero-kisses-promo"
                  aria-label="Kisses app"
                >
                  <img src="/apps/kisses-hero.jpg" alt="Kisses — pet ID promo" className="w-full h-full object-cover" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y border-white/5 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What We Build</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From stunning websites to powerful mobile apps, we bring your digital vision to life.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Web Development */}
            <div className="group relative bg-card/50 border border-white/5 rounded-2xl p-8 card-hover overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <Globe className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Web Development</h3>
                <p className="text-muted-foreground mb-6">
                  Custom websites that convert visitors into customers. Responsive, fast, and SEO-optimized for maximum impact.
                </p>
                <ul className="space-y-2 mb-6">
                  {['Custom Design', 'Mobile Responsive', 'SEO Optimized', 'Fast Loading'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services">
                  <Button variant="outline" className="gap-2">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* App Development */}
            <div className="group relative bg-card/50 border border-white/5 rounded-2xl p-8 card-hover overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <Smartphone className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3">App Development</h3>
                <p className="text-muted-foreground mb-6">
                  Native and cross-platform mobile apps that deliver exceptional user experiences on iOS and Android.
                </p>
                <ul className="space-y-2 mb-6">
                  {['iOS & Android', 'Cross-Platform', 'Push Notifications', 'App Store Ready'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services">
                  <Button variant="outline" className="gap-2">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-primary/5 to-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Choose <span className="text-primary">TryWeb</span>?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We combine cutting-edge AI technology with expert craftsmanship to deliver exceptional results.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-card/50 border border-white/5 rounded-xl p-6 card-hover"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Apps Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(34,197,94,0.08),transparent_50%),radial-gradient(circle_at_70%_100%,rgba(244,114,182,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-3">From our studio</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Apps we've built, shipped & love</h2>
            <p className="text-muted-foreground">
              We don't just build for clients — we build products of our own. Both apps below are live on the App Store and Google Play.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Stir */}
            <div className="group relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-emerald-500/10 via-card/60 to-amber-500/5 card-hover" data-testid="app-card-stir">
              <div className="relative p-8 sm:p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-zinc-900 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <img
                      src="https://customer-assets.emergentagent.com/job_c2c4f44d-f7c5-4fd0-bbd5-451d182ee18d/artifacts/b98ntxjh_App%20icon%20to%20use.png"
                      alt="Stir logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Stir</h3>
                    <p className="text-sm text-muted-foreground">Your personal cooking assistant</p>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Import recipes from TikTok, Instagram, YouTube or any website with a single tap. Plan meals, track 7 nutrients per serving, and let Stir auto-generate your weekly shopping list.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {['One-tap social import', 'AI chef assistant', 'Smart meal planner', 'Auto shopping lists'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 mb-6">
                  <a href="https://apps.apple.com/gb/app/stir-your-recipe-assistant/id6760202603" target="_blank" rel="noopener noreferrer" data-testid="stir-appstore-link">
                    <Button variant="outline" className="gap-2 border-white/20 hover:border-emerald-400/60 hover:bg-emerald-500/10">
                      <Smartphone className="w-4 h-4" /> App Store
                    </Button>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=io.tryweb.cookflow" target="_blank" rel="noopener noreferrer" data-testid="stir-googleplay-link">
                    <Button variant="outline" className="gap-2 border-white/20 hover:border-emerald-400/60 hover:bg-emerald-500/10">
                      <Smartphone className="w-4 h-4" /> Google Play
                    </Button>
                  </a>
                  <a href="https://stir-it.app" target="_blank" rel="noopener noreferrer" data-testid="stir-website-link">
                    <Button variant="ghost" className="gap-2">
                      Visit site <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center pt-6 border-t border-white/5">
                  <div><p className="text-xl font-bold text-emerald-400">1000+</p><p className="text-xs text-muted-foreground">Recipes imported</p></div>
                  <div><p className="text-xl font-bold text-emerald-400">7</p><p className="text-xs text-muted-foreground">Nutrients tracked</p></div>
                  <div><p className="text-xl font-bold text-emerald-400">30s</p><p className="text-xs text-muted-foreground">Avg import time</p></div>
                </div>
              </div>
            </div>

            {/* Kisses */}
            <div className="group relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-rose-300/10 via-card/60 to-emerald-200/5 card-hover" data-testid="app-card-kisses">
              <div className="relative p-8 sm:p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-white/90 flex items-center justify-center shadow-lg shadow-rose-300/20">
                    <img
                      src="https://kisses-id.app/assets/assets/images/icon.d174aa754565547125840e90e39f4bdf.png"
                      alt="Kisses logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Kisses</h3>
                    <p className="text-sm text-muted-foreground">The beautiful pet ID app</p>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  A soft, pastel pet-ID app. Health, schedule, vet contacts and house rules — all in one beautiful profile. Share with a tap via link, QR code or NFC. Lost-pet mode finds them if they ever wander.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {['Complete pet profile', 'Tap-to-share (NFC)', 'Lost-pet mode', 'Sitter & walker mode'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-rose-300 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 mb-6">
                  <a href="https://apps.apple.com/gb/app/kisses-pet-id-lost-pet-app/id6762553156" target="_blank" rel="noopener noreferrer" data-testid="kisses-appstore-link">
                    <Button variant="outline" className="gap-2 border-white/20 hover:border-rose-300/60 hover:bg-rose-300/10">
                      <Smartphone className="w-4 h-4" /> App Store
                    </Button>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=io.tryweb.kisses" target="_blank" rel="noopener noreferrer" data-testid="kisses-googleplay-link">
                    <Button variant="outline" className="gap-2 border-white/20 hover:border-rose-300/60 hover:bg-rose-300/10">
                      <Smartphone className="w-4 h-4" /> Google Play
                    </Button>
                  </a>
                  <a href="https://kisses-id.app/" target="_blank" rel="noopener noreferrer" data-testid="kisses-website-link">
                    <Button variant="ghost" className="gap-2">
                      Visit site <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center pt-6 border-t border-white/5">
                  <div><p className="text-xl font-bold text-rose-300">2k+</p><p className="text-xs text-muted-foreground">Pets registered</p></div>
                  <div><p className="text-xl font-bold text-rose-300">8</p><p className="text-xs text-muted-foreground">Countries</p></div>
                  <div><p className="text-xl font-bold text-rose-300">24/7</p><p className="text-xs text-muted-foreground">Lost-mode</p></div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/projects">
              <Button variant="outline" size="lg" className="gap-2" data-testid="view-portfolio-cta">
                See full portfolio <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it. Here's what business owners say about working with us.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-card/50 border border-white/5 rounded-xl p-6 card-hover text-center"
              >
                <div className="flex gap-1 mb-4 justify-center">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-sm">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to <span className="text-gradient">Transform</span> Your Business?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join the array of businesses that have elevated their online presence with TryWeb. 
            Get started today and see the difference AI-powered web development can make.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="glow-primary gap-2" data-testid="cta-pricing">
                View Pricing <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" data-testid="cta-contact">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default HomePage;
